import type { Invoice } from '@clockwise/clients/invoices';
import {
  INVOICE_EMAIL_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  type InvoiceEmailStatus,
  type InvoiceFlagSeverity,
  type InvoiceStatus,
  type InvoiceType,
} from '@clockwise/shared';
import {
  CheckCheck,
  Eye,
  Flag,
  Lock,
  type LucideIcon,
  MailMinus,
  MailX,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { type ReactElement, type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Status and type chips for an invoice, shared by the list, the detail view
 * and the approval queue so the three can never colour the same state
 * differently.
 *
 * Colours come from the semantic token classes (`muted`, `primary`,
 * `destructive`) wherever the theme defines one. It defines no success or info
 * token, so green and blue are Tailwind palette values with an explicit
 * `dark:` variant each — swap them for tokens if globals.css ever gains them.
 */

const STATUS_CLASSES: Record<InvoiceStatus, string> = {
  // Draft is deliberately the quietest: it is the default state and most of
  // the list is in it, so colouring it would make the page noise rather than
  // signal.
  draft: 'bg-muted text-muted-foreground',
  approved: 'bg-primary/10 text-primary',
  // Sent is terminal — the strongest chip on the page, because it is the one
  // state you cannot walk back.
  sent: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

export function InvoiceStatusBadge({
  status,
  className,
}: {
  status: InvoiceStatus;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn('border-transparent', STATUS_CLASSES[status], className)}
      title={status === 'sent' ? 'Sent to the customer and locked' : undefined}
    >
      {/* Sent is final — nothing on the invoice can change — so the chip
          carries a padlock wherever it appears: the list and the invoice. */}
      {status === 'sent' ? (
        <Lock className="size-3" aria-label="Locked" data-testid="invoice-sent-lock" />
      ) : null}
      {INVOICE_STATUS_LABELS[status]}
    </Badge>
  );
}

/**
 * Type chip. `null` is a real state — a draft with nothing billable on it yet
 * — and renders as an explicit "Unclassified" rather than an empty cell, so a
 * reader can tell it apart from a value that failed to load (G15).
 */
export function InvoiceTypeBadge({
  type,
  className,
}: {
  type: InvoiceType | null;
  className?: string;
}) {
  if (type === null) {
    return <span className={cn('text-xs text-muted-foreground', className)}>Unclassified</span>;
  }
  return (
    <Badge variant="secondary" className={className}>
      {INVOICE_TYPE_LABELS[type]}
    </Badge>
  );
}

/**
 * Review-flag colours, by severity.
 *
 * Semantic tokens, not raw palette values, so both themes track the design
 * tokens — and deliberately NOT the accent used for status: a flag is a
 * warning, and colouring it like a status chip would make the two read as the
 * same kind of fact.
 *
 * `info` is the quietest on purpose. FIRST_INVOICE is the most common flag by
 * far and is not a problem — colouring it like a fault would make the whole
 * list look broken on the first month a tenant generates.
 */
const FLAG_SEVERITY_CLASSES: Record<InvoiceFlagSeverity, string> = {
  info: 'bg-muted text-muted-foreground',
  review: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  blocking: 'bg-destructive/10 text-destructive',
};

/**
 * How many flags an invoice carries, coloured by its loudest one.
 *
 * A COUNT, not a list of reasons: the reasons live in the popover beside it,
 * and spelling them out here would make the column too wide to survive on a
 * laptop. Renders nothing at all when there are none — an explicit "0 flags"
 * on the overwhelming majority of rows is noise, and unlike an unclassified
 * type there is no ambiguity to resolve: no stripe and no chip reads
 * unmistakably as "nothing to look at".
 */
export function InvoiceFlagBadge({
  count,
  severity,
  className,
}: {
  count: number;
  /** The loudest severity across the invoice's flags. Null when the flags have
   *  not loaded yet, which renders neutrally rather than guessing at a colour
   *  that might turn out to be red. */
  severity: InvoiceFlagSeverity | null;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1 border-transparent tabular-nums',
        FLAG_SEVERITY_CLASSES[severity ?? 'info'],
        className,
      )}
    >
      <Flag className="size-3" aria-hidden="true" />
      {count}
    </Badge>
  );
}

/**
 * Email chip colours and icons, by the invoice's email status.
 *
 * Opened is blue — it is the news a finance user is looking for.
 * Delivered is green but quieter than the Sent STATUS chip beside it, which is
 * the same hue at the same weight. Bounced and Marked as spam are both
 * destructive: either way the customer may not have the invoice and someone
 * needs to act. Email sent stays muted: it is the waiting state.
 */
const EMAIL_STATUS_STYLES: Record<InvoiceEmailStatus, { className: string; icon: LucideIcon }> = {
  sent: { className: 'bg-muted text-muted-foreground', icon: Send },
  delivered: {
    className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    icon: CheckCheck,
  },
  // Blue, not `primary`: this theme's primary is near-black, which put the
  // one chip meant to stand out in the same grey as "Email sent".
  opened: { className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', icon: Eye },
  bounced: { className: 'bg-destructive/10 text-destructive', icon: MailX },
  complained: { className: 'bg-destructive/10 text-destructive', icon: ShieldAlert },
};

/** An instant from the API, in the viewer's timezone, to the minute. */
const EMAIL_AT_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/** Null for a missing value; "Unknown date" for one that will not parse, so a
 *  corrupt timestamp never reads as "not yet" (G15). */
function formatEmailAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Unknown date' : EMAIL_AT_FORMAT.format(d);
}

export type InvoiceEmailFields = Pick<
  Invoice,
  | 'status'
  | 'sentAt'
  | 'emailStatus'
  | 'emailDeliveredAt'
  | 'emailFirstOpenedAt'
  | 'emailLastOpenedAt'
  | 'emailOpenCount'
  | 'emailBouncedAt'
  | 'emailBounceReason'
  | 'emailComplainedAt'
>;

/**
 * What became of the invoice's email: ONE chip with the last meaningful
 * status — Opened, not "Sent, Delivered, Opened" — and the whole story on
 * hover, or on click / tap (touch has no hover): when it was sent, delivered, first and last opened, how many opens,
 * and any bounce or spam report.
 *
 * Renders nothing for an invoice that has not been sent. A sent invoice with
 * no email status was sent before tracking existed or marked sent by hand;
 * `untracked="label"` says so (the list, where an empty cell would read as a
 * failed load), `untracked="hidden"` stays quiet (the invoice header, where the
 * Sent banner already speaks).
 */
export function InvoiceEmailBadge({
  invoice,
  untracked = 'hidden',
  className,
}: {
  invoice: InvoiceEmailFields;
  untracked?: 'label' | 'hidden';
  className?: string;
}) {
  const status = invoice.emailStatus;
  // `== null`: an API older than this field leaves it undefined, which must
  // read as "not tracked", not crash the chip lookup below.
  if (status == null) {
    if (invoice.status !== 'sent' || untracked === 'hidden') return null;
    return (
      <DetailsOnHoverOrTap
        trigger={
          <button
            type="button"
            className={cn('cursor-pointer text-xs text-muted-foreground', className)}
            data-testid="invoice-email-untracked"
          >
            Not tracked
          </button>
        }
        className="max-w-xs"
      >
        Sent before email tracking began, or marked sent without being emailed.
      </DetailsOnHoverOrTap>
    );
  }

  const style = EMAIL_STATUS_STYLES[status];
  const Icon = style.icon;
  const label = INVOICE_EMAIL_STATUS_LABELS[status];
  return (
    <DetailsOnHoverOrTap
      trigger={
        <Badge
          variant="outline"
          asChild
          className={cn('cursor-pointer gap-1 border-transparent', style.className, className)}
        >
          <button
            type="button"
            aria-label={`Email: ${label}`}
            data-testid="invoice-email-badge"
            data-email-status={status}
          >
            <Icon className="size-3" aria-hidden="true" />
            {label}
          </button>
        </Badge>
      }
      className="max-w-sm"
      data-testid="invoice-email-details"
    >
      <InvoiceEmailDetails invoice={invoice} />
    </DetailsOnHoverOrTap>
  );
}

/**
 * ONE popover that opens two ways: a mouse hovering the trigger (a peek that
 * closes when the pointer leaves) and a click, tap or Enter (pinned until
 * dismissed). Touch screens have no hover, and "when was it last opened" has
 * to be reachable on an iPad too — so not a tooltip, and not a tooltip plus a
 * popover rendering the same content twice.
 *
 * The content holds nothing interactive, so focus never moves into it: a
 * hover must not steal focus, and closing returns nothing to re-trigger.
 */
function DetailsOnHoverOrTap({
  trigger,
  children,
  className,
  'data-testid': testId,
}: {
  trigger: ReactElement;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  const [open, setOpen] = useState<false | 'hover' | 'pinned'>(false);
  return (
    <Popover open={open !== false} onOpenChange={(next) => (next ? null : setOpen(false))}>
      <PopoverTrigger
        asChild
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') setOpen((o) => o || 'hover');
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') setOpen((o) => (o === 'hover' ? false : o));
        }}
        onClick={(e) => {
          // Our own toggle, not Radix's: a click on a hover-peek PINS it
          // rather than closing what the user is reading.
          e.preventDefault();
          setOpen((o) => (o === 'pinned' ? false : 'pinned'));
        }}
      >
        {trigger}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className={cn('w-auto p-3 text-xs', className)}
        data-testid={testId}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

function InvoiceEmailDetails({ invoice }: { invoice: InvoiceEmailFields }) {
  const opened = invoice.emailOpenCount > 0 || invoice.emailLastOpenedAt !== null;
  // Without a delivery date: a bounce means it was not delivered; an open or
  // a spam report proves it was (SNS can deliver those before the delivery
  // event, or SES may never report one). Otherwise "—", never "Not yet" —
  // an email sent while tracking was off would wait for a report forever.
  const deliveredFallback =
    invoice.emailStatus === 'bounced'
      ? 'No'
      : opened || invoice.emailStatus === 'complained'
        ? 'Yes'
        : '—';
  const rows: Array<[string, string]> = [
    ['Sent', formatEmailAt(invoice.sentAt) ?? 'Unknown date'],
    ['Delivered', formatEmailAt(invoice.emailDeliveredAt) ?? deliveredFallback],
  ];
  if (opened) {
    // First opened only when it adds something: with one open it IS the last.
    if (invoice.emailOpenCount > 1 && invoice.emailFirstOpenedAt) {
      rows.push(['First opened', formatEmailAt(invoice.emailFirstOpenedAt) ?? 'Unknown date']);
    }
    rows.push(['Last opened', formatEmailAt(invoice.emailLastOpenedAt) ?? 'Unknown date']);
    // A click with no open event (images blocked) would read "Opens 0".
    if (invoice.emailOpenCount > 0) rows.push(['Opens', String(invoice.emailOpenCount)]);
  } else {
    rows.push(['Opened', 'None recorded']);
  }
  if (invoice.emailBouncedAt) {
    rows.push(['Bounced', formatEmailAt(invoice.emailBouncedAt) ?? 'Unknown date']);
  }
  if (invoice.emailComplainedAt) {
    rows.push(['Marked as spam', formatEmailAt(invoice.emailComplainedAt) ?? 'Unknown date']);
  }

  return (
    <div className="space-y-1.5 py-0.5">
      <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5">
        {rows.map(([term, value]) => (
          <div key={term} className="contents">
            <dt className="opacity-70">{term}</dt>
            <dd className="text-right tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {invoice.emailBounceReason ? (
        <p className="flex gap-1.5 border-t border-current/20 pt-1.5">
          <MailMinus className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span className="break-words">{invoice.emailBounceReason}</span>
        </p>
      ) : null}
      {opened ? (
        <p className="border-t border-current/20 pt-1.5 opacity-70">
          Opens are approximate: some mail apps load images on arrival, others block them.
        </p>
      ) : null}
    </div>
  );
}
